/**
 * LiveActivityFeed - shows what PriceGuard is doing in the background,
 * anonymised. Goal: visitors see real momentum (price drops detected,
 * receipts uploaded, alerts fired) without any personal data leaking.
 *
 * Rendered on Landing (prominently, public) and on Dashboard (gives
 * users a sense they're part of a larger thing). Pulls from the public
 * endpoint `/api/public/live-activity`, no auth required.
 *
 * Privacy contract:
 *   - No user names, emails, addresses, or order numbers
 *   - No receipt images or exact product names
 *   - Drop amounts are real; product context is retailer + category only
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Receipt, Trophy, Eye, ArrowDown, ShieldCheck } from "@phosphor-icons/react";
import CountUp from "./CountUp";

const API = process.env.REACT_APP_BACKEND_URL;


/**
 * Best-effort "X minutes/hours/days ago" using only the alert
 * timestamp - keeps the feed feeling alive without a heavy date lib.
 */
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return "this month";
}


export default function LiveActivityFeed({ variant = "landing" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/live-activity`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent - section just hides */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const counts = data.counts || {};
  const drops = data.drops || [];
  const big = data.biggest_drop_this_week || 0;
  const bigAllTime = data.biggest_drop_all_time || 0;

  // If there's literally nothing yet (brand-new install), hide the
  // section rather than show zero-everywhere.
  const totalSignal = (counts.tracked_total || 0)
    + (counts.alerts_all_time || 0)
    + drops.length;
  if (totalSignal === 0) return null;

  const isDashboard = variant === "dashboard";

  return (
    <section
      className={isDashboard ? "mt-10" : "border-t border-[#e4e4e7] bg-[#0a0a0a] text-white"}
      data-testid="live-activity-feed"
    >
      <div className={isDashboard ? "" : "max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24"}>
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 ${isDashboard ? "mb-6" : "mb-10"}`}>
          <div>
            <div className={`pg-overline ${isDashboard ? "text-[#16A34A]" : "text-[#16A34A]"}`}>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                Live activity · updated {timeAgo(data.generated_at)}
              </span>
            </div>
            <h2 className={`font-heading font-black tracking-tighter mt-3 ${isDashboard ? "text-2xl" : "text-4xl lg:text-5xl"} leading-[1.05]`}>
              {isDashboard ? (
                <>
                  PriceGuard is watching<span className="text-[#16A34A]">.</span>
                </>
              ) : (
                <>
                  Right now, in the background<span className="text-[#16A34A]">.</span>
                </>
              )}
            </h2>
            {!isDashboard && (
              <p className={`mt-4 max-w-2xl leading-relaxed text-white/70`}>
                A live, anonymised snapshot of what PriceGuard is detecting across the NZ retail ecosystem. Healthy price movement is good news for shoppers and retailers alike. No user names, no order numbers - just the work happening in the background for everyone signed up.
              </p>
            )}
          </div>
        </div>

        {/* Six stat tiles - colour-graded for scannability. The biggest-
            drop tile lands on green to draw the eye since it's the most
            motivating proof point on the page. */}
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px border ${isDashboard ? "bg-[#e4e4e7] border-[#e4e4e7]" : "bg-white/10 border-white/10"}`}>
          <StatTile
            isDashboard={isDashboard}
            accent="#16A34A"
            icon={Trophy}
            label="Biggest drop this week"
            primary={`$${Math.round(big).toLocaleString()}`}
            primaryIsMoney
            sub={bigAllTime > big ? `all-time high $${Math.round(bigAllTime).toLocaleString()}` : "fresh this week"}
          />
          <StatTile
            isDashboard={isDashboard}
            accent="#FF3B30"
            icon={ArrowDown}
            label="Drops detected this week"
            primary={counts.alerts_this_week || 0}
            sub={counts.alerts_all_time ? `${counts.alerts_all_time.toLocaleString()} all time` : "since launch"}
          />
          <StatTile
            isDashboard={isDashboard}
            accent="#002FA7"
            icon={Eye}
            label="Tracked purchases"
            primary={counts.tracked_total || 0}
            sub="Recent buys · price-drop watch"
          />
          <StatTile
            isDashboard={isDashboard}
            accent="#D97706"
            icon={Heart}
            label="Wishlist items"
            primary={counts.wishlist_being_watched || 0}
            sub="Future buys · target-price watch"
          />
          <StatTile
            isDashboard={isDashboard}
            accent="#A1A1AA"
            icon={Receipt}
            label="Receipts filed this week"
            primary={counts.receipts_this_week || 0}
            sub={counts.receipts_all_time ? `${counts.receipts_all_time.toLocaleString()} all time` : "since launch"}
          />
          <StatTile
            isDashboard={isDashboard}
            accent="#16A34A"
            icon={ShieldCheck}
            label="Warranties on watch"
            primary={counts.warranties_on_watch || 0}
            sub="CGA alerts armed"
          />
        </div>

        {/* Recent drops feed moved into the hero "Today on PriceGuard"
            panel so we don't show the same anonymised drops twice. This
            section now focuses purely on aggregate stats. */}

        {!isDashboard && (
          <p className="text-[11px] mt-8 font-mono-pg uppercase tracking-widest text-white/40 max-w-2xl">
            Privacy-safe by design · no user names, addresses, receipt images or order numbers · we only ever show retailer + category + drop amount
          </p>
        )}
      </div>
    </section>
  );
}


function StatTile({ isDashboard, accent, invertOnDark, icon: Icon, label, primary, primaryIsMoney, sub }) {
  // Dashboard variant = white tiles on a light grid. Landing variant =
  // dark tiles on the black background. Icon + number colour-coded per tile.
  const bg = isDashboard ? "bg-white" : (invertOnDark ? "bg-white text-[#0a0a0a]" : "bg-[#0a0a0a]");
  const labelColour = isDashboard ? "#52525b" : (invertOnDark ? "#52525b" : "rgba(255,255,255,0.5)");
  const subColour = isDashboard ? "#52525b" : (invertOnDark ? "#52525b" : "rgba(255,255,255,0.45)");
  const numericPrimary = typeof primary === "number" ? primary : null;
  return (
    <div className={`${bg} p-4 lg:p-5 flex flex-col gap-2`} data-testid={`live-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="pg-overline" style={{ color: labelColour }}>{label}</div>
        <Icon size={16} weight="duotone" color={accent} />
      </div>
      <div className="font-heading text-3xl lg:text-4xl font-black tracking-tighter" style={{ color: accent }}>
        {numericPrimary !== null ? (
          <CountUp value={numericPrimary} duration={1100} />
        ) : (
          primary
        )}
      </div>
      {sub && (
        <div className="text-[11px] font-mono-pg uppercase tracking-widest" style={{ color: subColour }}>
          {sub}
        </div>
      )}
    </div>
  );
}
